// components/AI/useChatTextFiles.js
// [TEXT ATTACH] Lampiran BERKAS TEKS untuk mode Coding — pasangan dari
// useChatImages, dan sengaja dibuat sebagai hook TERPISAH, bukan cabang di
// dalamnya.
//
// Alasannya bukan kerapian, tapi karena keduanya bergerak berbeda di tiga
// tempat yang menentukan:
//
//   1. Gambar dikirim sebagai `images: [{format, b64}]` di luar `question`.
//      Teks TIDAK punya jalur sendiri di /api/ai/ask — ia harus menyatu ke
//      dalam `question`. Menggabungkan keduanya berarti satu hook memegang dua
//      bentuk muatan yang tak sebanding.
//   2. Gambar dikecilkan (canvas, 1600px, JPEG 0.82). Teks tidak boleh
//      disentuh sama sekali — satu byte berubah dan analisa log jadi salah.
//   3. Gambar menyimpan turunan kecil ke riwayat (thumb/preview). Teks tidak
//      menyimpan isinya sama sekali ke riwayat, hanya nama dan ukuran.
//
// BATAS 50 KB ADALAH INTI RANCANGANNYA, bukan angka penjagaan sembarangan.
// Berkas di bawahnya masuk UTUH ke dalam pertanyaan, jadi model melihatnya
// tanpa perlu memanggil tool apa pun dan tanpa perlu namanya disebut. Di
// atasnya, cara itu berhenti masuk akal: MAX_BYTES pada read_file di ai-service
// adalah 80 KB per panggilan, jadi berkas besar memang dirancang untuk dibaca
// BERTAHAP — dan itu pekerjaan panel Code sandbox, bukan pekerjaan kolom input.
//
// Karena itu berkas >50 KB DITOLAK, dan penolakannya menunjuk ke sandbox.
// Menolak tanpa menunjuk ke mana-mana akan membuat jalur ini terasa buntu
// padahal jalurnya sudah ada dan sudah berfungsi.
import { useState, useRef, useCallback, useEffect } from 'react';

// Ekstensi yang diterima. SENGAJA lebih sempit dari ALLOWED_EXT di
// ai-service/aiTools.js: daftar itu mengatur apa yang boleh DIBACA dari disk
// (termasuk seluruh berkas sumber), sementara daftar ini mengatur apa yang
// wajar DITEMPELKAN ke pertanyaan. Kode sumber tidak ada di sini dengan alasan
// yang jelas — untuk itu sudah ada Project root dan read_file, yang tidak
// membebani setiap giliran percakapan dengan isi berkas yang sama.
export const TEXT_EXT_RE = /\.(log|csv|json|md|txt)$/i;

// 50 KB. Lihat catatan panjang di kepala berkas — ini bukan batas keamanan,
// ini garis antara "muat sekali lihat" dan "harus dibaca bertahap".
export const TEXT_MAX_BYTES = 50 * 1024;

// Batas jumlah. Lebih rendah dari CHAT_IMG_MAX (8) karena bobotnya jauh
// berbeda: 4 berkas teks bisa berarti 200 KB masuk prompt, sementara 8 gambar
// setelah dikecilkan jauh lebih ringan dari itu.
export const TEXT_MAX_FILES = 4;

export function humanBytes(n) {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

function readAsText(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload  = () => resolve(String(fr.result || ''));
    fr.onerror = () => reject(new Error('read failed'));
    // UTF-8 eksplisit. Log dari server Linux hampir selalu UTF-8; membiarkan
    // browser menebak bisa menghasilkan mojibake yang terlihat seperti berkas
    // rusak padahal cuma salah tafsir encoding.
    fr.readAsText(file, 'UTF-8');
  });
}

export function useChatTextFiles({ enabled, onError }) {
  const [files, setFiles] = useState([]);   // [{ id, name, bytes, text }]
  const [busy, setBusy]   = useState(false);
  const inputRef = useRef(null);
  const seqRef   = useRef(0);

  // Keluar dari mode Coding harus membuang lampiran. Sama alasannya dengan
  // useChatImages: tanpa ini berkas "menempel" dan bisa terkirim tanpa
  // disadari saat user kembali.
  useEffect(() => {
    if (!enabled) setFiles([]);
  }, [enabled]);

  const addFiles = useCallback(async (fileList) => {
    if (!enabled) return;
    const picked = Array.from(fileList || []);
    if (!picked.length) return;

    setBusy(true);
    try {
      const added = [];
      for (const f of picked) {
        const name = f.name || 'file';
        if (!TEXT_EXT_RE.test(name)) {
          onError?.('ext', { name });
          continue;
        }
        // Penolakan ukuran menunjuk ke sandbox — lihat catatan di kepala
        // berkas. Ini satu-satunya pesan galat di sini yang WAJIB menyebut
        // jalan keluar, karena berkas besar justru kasus yang paling sering.
        if (f.size > TEXT_MAX_BYTES) {
          onError?.('size', { name, bytes: f.size });
          continue;
        }
        let text;
        try { text = await readAsText(f); }
        catch (_) { onError?.('read', { name }); continue; }

        // Berkas biner yang kebetulan bernama .log/.txt. FileReader tidak
        // pernah gagal untuk ini — ia menghasilkan U+FFFD, dan hasilnya
        // membakar token tanpa berarti apa pun. Ditolak di sini, bukan
        // dibiarkan sampai ke model.
        if (text.indexOf('\uFFFD') !== -1) {
          onError?.('binary', { name });
          continue;
        }

        seqRef.current += 1;
        added.push({ id: `txt-${seqRef.current}`, name, bytes: f.size, text });
      }
      if (added.length) {
        setFiles(prev => {
          const next = [...prev, ...added].slice(0, TEXT_MAX_FILES);
          if (prev.length + added.length > TEXT_MAX_FILES) {
            onError?.('count', { max: TEXT_MAX_FILES });
          }
          return next;
        });
      }
    } finally { setBusy(false); }
  }, [enabled, onError]);

  const removeFile = useCallback((id) => {
    setFiles(prev => prev.filter(a => a.id !== id));
  }, []);

  const clearFiles = useCallback(() => setFiles([]), []);
  const openPicker = useCallback(() => inputRef.current?.click(), []);

  // Sama jebakannya dengan useChatImages.onFileSelected: `e.target.files`
  // adalah referensi LIVE. Menyalin ke array HARUS dilakukan sebelum
  // `e.target.value = ''`, atau daftarnya ikut kosong dan handler tak pernah
  // menerima apa pun — gagal yang sepenuhnya senyap.
  const onFileSelected = useCallback((e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length) addFiles(picked);
  }, [addFiles]);

  // Isi berkas → blok yang ditempelkan ke `question`.
  //
  // Pembatasnya memakai pagar tiga-backtick dengan label nama berkas. Itu
  // bukan hiasan: tanpa pembatas yang jelas, isi log yang memuat kalimat
  // berbahasa manusia bisa terbaca model sebagai bagian dari instruksi user.
  const toPromptBlocks = useCallback(() => {
    if (!enabled || !files.length) return '';
    return files.map(f =>
      `\n\n--- Isi berkas terlampir: ${f.name} (${humanBytes(f.bytes)}) ---\n`
      + '```\n' + f.text + '\n```'
    ).join('');
  }, [enabled, files]);

  // Jejak untuk transkrip & riwayat: nama dan ukuran SAJA.
  //
  // Isinya SENGAJA tidak ikut. Log 50 KB yang disimpan ke ai_chat_history akan
  // menumpuk cepat, dan karena backup memakai `VACUUM INTO` yang menyalin
  // SELURUH database, setiap backup ikut membengkak — persis alasan yang
  // membuat gambar hanya disimpan sebagai miniatur.
  const toFileMarks = useCallback(
    () => (enabled ? files.map(f => ({ name: f.name, bytes: f.bytes })) : []),
    [enabled, files],
  );

  return {
    files, busy, inputRef,
    addFiles, removeFile, clearFiles, openPicker, onFileSelected,
    toPromptBlocks, toFileMarks,
    atMax: files.length >= TEXT_MAX_FILES,
  };
}
